import { BrowserRouter, Routes, Route, Navigate } from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "@/lib/auth-context";
import { ProtectedRoute, PublicOnlyRoute } from "@/components/protected-route";
import { Toaster } from "@/components/ui/toaster";
import { DashboardLayout } from "@/components/layout/dashboard-layout";
import { Login } from "@/routes/Login";
import { AcceptInvite } from "@/routes/AcceptInvite";
import { Overview } from "@/routes/Overview";
import { Ecom } from "@/routes/Ecom";
import { Markten } from "@/routes/Markten";
import { Brands } from "@/routes/Brands";
import { Platforms } from "@/routes/Platforms";
import { Campagnes } from "@/routes/Campagnes";
import { Awareness } from "@/routes/Awareness";
import { Jobs } from "@/routes/Jobs";
import { Collaborations } from "@/routes/Collaborations";
import { Benchmarks } from "@/routes/Benchmarks";
import { Sources } from "@/routes/Sources";
import { Transfer } from "@/routes/Transfer";
import { Settings } from "@/routes/Settings";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: 1,
      // Throw only on initial-load failures (no cached data yet) so the route
      // error boundary can show a clean error state; background refetch errors
      // keep the last good data on screen.
      throwOnError: (_error, query) => query.state.data === undefined,
    },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter basename={import.meta.env.VITE_ROUTER_BASENAME || undefined}>
          <Routes>
            <Route path="/login" element={<PublicOnlyRoute><Login /></PublicOnlyRoute>} />
            <Route path="/invite/:token" element={<PublicOnlyRoute><AcceptInvite /></PublicOnlyRoute>} />

            <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route index element={<Overview />} />
              <Route path="/benchmarks" element={<Benchmarks />} />
              <Route path="/markten" element={<Markten />} />
              <Route path="/ecom" element={<Ecom />} />
              <Route path="/brands" element={<Brands />} />
              <Route path="/platforms" element={<Platforms />} />
              <Route path="/campagnes" element={<Campagnes />} />
              <Route path="/awareness" element={<Awareness />} />
              <Route path="/jobs" element={<Jobs />} />
              <Route path="/collaborations" element={<Collaborations />} />
              <Route path="/sources" element={<Sources />} />
              <Route path="/transfer" element={<Transfer />} />
              <Route path="/settings" element={<Settings />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </AuthProvider>
    </QueryClientProvider>
  );
}
